import { lazy, Suspense, type ComponentType } from 'react';
import { Route, Routes } from 'react-router-dom';
import { RequireAuth } from '../features/auth/RequireAuth';
import { RequireRole } from '../features/auth/RequireRole';
import { AppShell } from '../components/AppShell';
import { ErrorBoundary } from '../components/ErrorBoundary';
import { ToastProvider } from '../components/Toast';

/**
 * Rotas em `lazy` → cada página vira um chunk carregado sob demanda.
 * Antes tudo era import estático e caía num bundle único (> 2 MB). Agora
 * o carregamento inicial traz só o shell + a rota aberta; o resto chega
 * conforme o usuário navega. Named exports usam o adaptador `.then`.
 *
 * Wrappers (RequireAuth/RequireRole/AppShell/ErrorBoundary/ToastProvider)
 * ficam estáticos — são leves e usados em quase toda navegação.
 */
const named = (loader: () => Promise<any>, key: string) =>
  lazy(() => loader().then((m) => ({ default: m[key] as ComponentType })));

const LoginPage = named(() => import('../features/auth/LoginPage'), 'LoginPage');
const SignupPage = named(() => import('../features/auth/SignupPage'), 'SignupPage');
const RequestPasswordResetPage = named(() => import('../features/auth/RequestPasswordResetPage'), 'RequestPasswordResetPage');
const ResetPasswordPage = named(() => import('../features/auth/ResetPasswordPage'), 'ResetPasswordPage');
const AcceptInvitePage = named(() => import('../features/auth/AcceptInvitePage'), 'AcceptInvitePage');
const ProfilePage = named(() => import('../features/profile/ProfilePage'), 'ProfilePage');
const DashboardPage = named(() => import('../features/dashboard/DashboardPage'), 'DashboardPage');
const SchedulePage = named(() => import('../features/schedule/SchedulePage'), 'SchedulePage');
const PatientsPage = named(() => import('../features/patients/PatientsPage'), 'PatientsPage');
const PatientDetailPage = named(() => import('../features/patients/PatientDetailPage'), 'PatientDetailPage');
const CrmPage = named(() => import('../features/crm/CrmPage'), 'CrmPage');
const ReconciliationPage = named(() => import('../features/reconciliation/ReconciliationPage'), 'ReconciliationPage');
const ContasPagarPage = named(() => import('../features/expenses/ContasPagarPage'), 'ContasPagarPage');
const OutrasReceitasPage = named(() => import('../features/incomes/OutrasReceitasPage'), 'OutrasReceitasPage');
const AdminHubPage = named(() => import('../features/admin-hub/AdminHubPage'), 'AdminHubPage');
const ServicesPage = named(() => import('../features/services/ServicesPage'), 'ServicesPage');
const ServiceDetailPage = named(() => import('../features/services/ServiceDetailPage'), 'ServiceDetailPage');
const EstoquePage = named(() => import('../features/inventory/EstoquePage'), 'EstoquePage');
const AgentInboxPage = named(() => import('../features/agent/AgentInboxPage'), 'AgentInboxPage');
const InjectablePresentationWindow = named(() => import('../features/assessments/InjectablePresentationWindow'), 'InjectablePresentationWindow');
const TreatmentPlansPage = named(() => import('../features/treatment-plans/TreatmentPlansPage'), 'TreatmentPlansPage');
const TreatmentPlanDetailPage = named(() => import('../features/treatment-plans/TreatmentPlanDetailPage'), 'TreatmentPlanDetailPage');
const TreatmentPlanTemplatesPage = named(() => import('../features/treatment-plans/TreatmentPlanTemplatesPage'), 'TreatmentPlanTemplatesPage');
const TreatmentPlanTemplateDetailPage = named(() => import('../features/treatment-plans/TreatmentPlanTemplateDetailPage'), 'TreatmentPlanTemplateDetailPage');
const InvoicesPage = named(() => import('../features/invoices/InvoicesPage'), 'InvoicesPage');
const AttachmentsPage = named(() => import('../features/attachments/AttachmentsPage'), 'AttachmentsPage');
const RecordsPage = named(() => import('../features/records/RecordsPage'), 'RecordsPage');
const AestheticAssessmentPage = named(() => import('../features/assessments/AestheticAssessmentPage'), 'AestheticAssessmentPage');
const TeamPage = named(() => import('../features/team/TeamPage'), 'TeamPage');
const ConsentPage = named(() => import('../features/consent/ConsentPage'), 'ConsentPage');
const ReportsPage = named(() => import('../features/reports/ReportsPage'), 'ReportsPage');
const CampaignsPage = named(() => import('../features/campaigns/CampaignsPage'), 'CampaignsPage');
// Aposentado (v1.7.1): /templates escondido. Página preservada em
// features/templates/MessageTemplatesPage.tsx; reimportar p/ religar.
const SettingsPage = named(() => import('../features/settings/SettingsPage'), 'SettingsPage');
const SupportPage = named(() => import('../features/support/SupportPage'), 'SupportPage');
const IntegrationsMarketplacePage = named(() => import('../features/integrations/IntegrationsMarketplacePage'), 'IntegrationsMarketplacePage');
// Aposentado (v1.7.1): /marketplace escondido. Página preservada em
// features/marketplace/MarketplacePage.tsx; reimportar p/ religar.
const WaitingRoomTvPage = named(() => import('../features/waiting-room/WaitingRoomTvPage'), 'WaitingRoomTvPage');
const NotFoundPage = named(() => import('../features/errors/NotFoundPage'), 'NotFoundPage');
const PublicProfilePage = named(() => import('../features/public/PublicProfilePage'), 'PublicProfilePage');
const SelfCheckinPage = named(() => import('../features/public/SelfCheckinPage'), 'SelfCheckinPage');
const SurveyPage = named(() => import('../features/public/SurveyPage'), 'SurveyPage');
const AgentSurveyPage = named(() => import('../features/public/AgentSurveyPage'), 'AgentSurveyPage');
const PublicBookingPage = named(() => import('../features/public/PublicBookingPage'), 'PublicBookingPage');
const PatientPortalPage = named(() => import('../features/public/PatientPortalPage'), 'PatientPortalPage');
const AdminOpsPage = named(() => import('../features/admin/AdminOpsPage'), 'AdminOpsPage');
const LandingPage = named(() => import('../features/marketing/LandingPage'), 'LandingPage');
const RoadmapPage = named(() => import('../features/marketing/RoadmapPage'), 'RoadmapPage');
const HelpPage = named(() => import('../features/help/HelpPage'), 'HelpPage');
const StatusPage = named(() => import('../features/status/StatusPage'), 'StatusPage');
const TermsPage = named(() => import('../features/legal/TermsPage'), 'TermsPage');
const PrivacyPage = named(() => import('../features/legal/PrivacyPage'), 'PrivacyPage');
const LgpdPage = named(() => import('../features/legal/LgpdPage'), 'LgpdPage');

/** Fallback enquanto o chunk da rota carrega. Discreto, sem layout shift. */
function RouteFallback() {
  return (
    <div
      className="min-h-[60vh] flex items-center justify-center"
      role="status"
      aria-label="Carregando"
    >
      <span className="w-6 h-6 rounded-full border-2 border-ink-200 border-t-brand-600 animate-spin" />
    </div>
  );
}

export function App() {
  return (
    <ToastProvider>
      <ErrorBoundary>
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            {/* Landing pública — pra prospect. Se já tem usuário logado, */}
            {/* a LandingPage redireciona pra /painel internamente. */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/signup" element={<SignupPage />} />
            {/* Recuperação de senha — telas públicas (link "Esqueci a senha"). */}
            <Route
              path="/password/request-reset"
              element={<RequestPasswordResetPage />}
            />
            <Route path="/password/reset" element={<ResetPasswordPage />} />
            {/* Aceite de convite de equipe — link do e-mail, sem auth. */}
            <Route path="/invite/accept" element={<AcceptInvitePage />} />
            {/* Perfil público do profissional — sem auth. */}
            <Route path="/p/:userId" element={<PublicProfilePage />} />
            {/* Agendamento público (patient self-service). */}
            <Route path="/p/:userId/agendar" element={<PublicBookingPage />} />
            {/* Sala de espera digital — self check-in via QR. */}
            <Route path="/checkin/:tenantId" element={<SelfCheckinPage />} />
            <Route
              path="/sala-espera/tv/:tenantId"
              element={<WaitingRoomTvPage />}
            />
            {/* Portal público do paciente — histórico de consultas/faturas. */}
            <Route path="/portal/:token" element={<PatientPortalPage />} />
            <Route path="/pesquisa/:token" element={<SurveyPage />} />
            {/* NPS do atendimento do agente de IA (escala 0–10, por link). */}
            <Route path="/avaliar-atendimento/:token" element={<AgentSurveyPage />} />
            {/* Status do sistema — público, lê health checks reais. */}
            <Route path="/status" element={<StatusPage />} />
            {/* Apresentação de injetáveis — janela read-only pra segunda tela. */}
            {/* Recebe os dados da sessão via postMessage (mesmo origin). */}
            <Route path="/apresentar/injetaveis" element={<InjectablePresentationWindow />} />
            {/* Roadmap público — timeline de versões (linkado na landing). */}
            <Route path="/roadmap" element={<RoadmapPage />} />
            {/* Páginas legais — públicas (linkadas no rodapé da landing). */}
            <Route path="/termos" element={<TermsPage />} />
            <Route path="/privacidade" element={<PrivacyPage />} />
            <Route path="/lgpd" element={<LgpdPage />} />
            {/* Admin Ops — vendor cross-tenant. Auth via x-admin-token. */}
            <Route path="/admin" element={<AdminOpsPage />} />
            {/* Avaliação estética — PÁGINA DEDICADA (tela cheia, sem sidebar), */}
            {/* pensada pra abrir em nova aba do prontuário. Autenticada. */}
            <Route
              path="/avaliacao/:patientId"
              element={
                <RequireAuth>
                  <AestheticAssessmentPage />
                </RequireAuth>
              }
            />
            {/* Layout autenticado (pathless) — AppShell renderiza <Outlet /> */}
            {/* pra cada rota filha. Caminhos absolutos (com `/`) garantem que */}
            {/* a landing pública em `/` não conflite com este grupo. */}
            <Route
              element={
                <RequireAuth>
                  <AppShell />
                </RequireAuth>
              }
            >
              <Route path="/painel" element={<DashboardPage />} />
              <Route path="agenda" element={<SchedulePage />} />
              <Route
                path="atendimentos-ia"
                element={<RequireRole path="/atendimentos-ia"><AgentInboxPage /></RequireRole>}
              />
              <Route
                path="crm"
                element={<RequireRole path="/crm"><CrmPage /></RequireRole>}
              />
              <Route
                path="conciliacao"
                element={<RequireRole path="/conciliacao"><ReconciliationPage /></RequireRole>}
              />
              <Route
                path="contas-a-pagar"
                element={<RequireRole path="/contas-a-pagar"><ContasPagarPage /></RequireRole>}
              />
              <Route
                path="outras-receitas"
                element={<RequireRole path="/outras-receitas"><OutrasReceitasPage /></RequireRole>}
              />
              <Route path="administracao" element={<AdminHubPage />} />
              <Route path="pacientes" element={<PatientsPage />} />
              <Route path="pacientes/:id" element={<PatientDetailPage />} />
              <Route path="servicos" element={<ServicesPage />} />
              <Route path="servicos/:id" element={<ServiceDetailPage />} />
              <Route
                path="estoque"
                element={<RequireRole path="/estoque"><EstoquePage /></RequireRole>}
              />
              {/* Rotas restritas por papel: RequireRole espelha o
                  PAGE_ACCESS (que espelha os @Roles do backend). */}
              <Route
                path="planos"
                element={<RequireRole path="/planos"><TreatmentPlansPage /></RequireRole>}
              />
              <Route
                path="planos/pacotes"
                element={<RequireRole path="/planos"><TreatmentPlanTemplatesPage /></RequireRole>}
              />
              <Route
                path="planos/pacotes/:id"
                element={<RequireRole path="/planos"><TreatmentPlanTemplateDetailPage /></RequireRole>}
              />
              <Route
                path="planos/:id"
                element={<RequireRole path="/planos"><TreatmentPlanDetailPage /></RequireRole>}
              />
              <Route path="faturas" element={<InvoicesPage />} />
              <Route
                path="equipe"
                element={<RequireRole path="/equipe"><TeamPage /></RequireRole>}
              />
              <Route path="arquivos" element={<AttachmentsPage />} />
              <Route
                path="prontuario"
                element={<RequireRole path="/prontuario"><RecordsPage /></RequireRole>}
              />
              <Route path="consentimento" element={<ConsentPage />} />
              <Route
                path="relatorios"
                element={<RequireRole path="/relatorios"><ReportsPage /></RequireRole>}
              />
              <Route
                path="campanhas"
                element={<RequireRole path="/campanhas"><CampaignsPage /></RequireRole>}
              />
              {/* Rotas /templates e /marketplace aposentadas (v1.7.1):
                  fora do menu e das rotas; URL direta cai no NotFound.
                  Páginas e backend preservados. Ver AppShell.tsx. */}
              <Route path="perfil" element={<ProfilePage />} />
              <Route
                path="configuracoes"
                element={<RequireRole path="/configuracoes"><SettingsPage /></RequireRole>}
              />
              <Route path="suporte" element={<SupportPage />} />
              <Route path="ajuda" element={<HelpPage />} />
              <Route
                path="integracoes"
                element={<RequireRole path="/integracoes"><IntegrationsMarketplacePage /></RequireRole>}
              />
            </Route>
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
      </ErrorBoundary>
    </ToastProvider>
  );
}
